import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, RotateCcwIcon } from 'lucide-react';
import { demoChapters, demoIntro, demoSteps, type DemoChapterId } from '../../data/demoTour';
const CHAPTER_ORDER: DemoChapterId[] = ['loisirs', 'autres'];
const EASE = [0.23, 1, 0.32, 1] as const;

/**
 * Bandeau de démonstration : sert de fil conducteur pour un visiteur autonome.
 * Il annonce l'étape en cours, dit quoi essayer, et permet de sauter
 * directement à n'importe quel module.
 */
export function DemoBanner() {
  const navigate = useNavigate();
  const {
    pathname
  } = useLocation();
  const [index, setIndex] = useState(0);
  const [visited, setVisited] = useState<string[]>([demoSteps[0].id]);
  const [open, setOpen] = useState(false);
  const step = demoSteps[index];
  const onStepPage = useMemo(() => pathname === step.path, [pathname, step.path]);
  const goTo = (next: number, {
    navigateTo = true
  }: {
    navigateTo?: boolean;
  } = {}) => {
    const bounded = Math.max(0, Math.min(demoSteps.length - 1, next));
    const target = demoSteps[bounded];
    setIndex(bounded);
    setVisited(current => current.includes(target.id) ? current : [...current, target.id]);
    if (navigateTo && pathname !== target.path) navigate(target.path);
  };
  const restart = () => {
    setVisited([demoSteps[0].id]);
    setOpen(false);
    goTo(0);
  };
  return <div data-no-translate className="relative z-50 w-full bg-ville-deep text-white" data-id="element-1119" data-mp="1upyoaalpnl6h">
      <div className="mx-auto flex max-w-[1500px] flex-wrap items-center gap-x-4 gap-y-2 px-5 py-2.5 lg:flex-nowrap lg:px-10" data-id="element-1120" data-mp="1pp1a093tnba9">
        <span className="shrink-0 rounded-full bg-ville-lime px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-[0.12em] text-ville-deep" data-id="element-1121" data-mp="1kq7t06pbt25c">
          Démo
        </span>

        <p className="min-w-0 flex-1 text-sm leading-snug text-white/90" data-id="element-1122" data-mp="9aj6fgfzi552">
          <span className="font-bold text-white" data-id="element-1123" data-mp="as84kfh4kw3c">
            Étape {index + 1}/{demoSteps.length} · {step.module}
          </span>
          {step.chapter === 'loisirs' && <span className="ml-2 rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-ville-lime" data-id="element-1124" data-mp="fpglkcqjeu30">
              Focus loisirs
            </span>}
          <span className="mx-2 hidden text-white/30 sm:inline" aria-hidden="true" data-id="element-1125" data-mp="6szb7bpzz08b">
            |
          </span>
          <span className="block sm:inline" data-id="element-1126" data-mp="3sycs4pqfswvi">{step.action}</span>
        </p>

        <div className="flex shrink-0 items-center gap-2" data-id="element-1127" data-mp="3t0fqbx1qtwbb">
          {!onStepPage && <button type="button" onClick={() => navigate(step.path)} className="rounded-full bg-ville-coral px-3.5 py-1.5 text-xs font-bold text-white transition-colors duration-150 ease-ville hover:bg-[#d2453b] focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-1128" data-mp="3f8srbpgf29ou">
              Ouvrir l’étape
            </button>}

          <div className="flex items-center gap-1" data-id="element-1129" data-mp="2qmbk7q8omfnv">
            <button type="button" onClick={() => goTo(index - 1)} disabled={index === 0} aria-label="Étape précédente" className="grid h-8 w-8 place-items-center rounded-full border border-white/25 text-white transition-colors duration-150 ease-ville hover:bg-white/10 disabled:opacity-35 disabled:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-1130" data-mp="2lni9u6ooz4rx">
              <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1131" data-mp="3jaqygajzoty5" />
            </button>
            <button type="button" onClick={() => goTo(index + 1)} disabled={index === demoSteps.length - 1} aria-label="Étape suivante" className="grid h-8 w-8 place-items-center rounded-full border border-white/25 text-white transition-colors duration-150 ease-ville hover:bg-white/10 disabled:opacity-35 disabled:hover:bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-1132" data-mp="3jcl9mx5gl7p1">
              <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1133" data-mp="1qk6h51w3aisc" />
            </button>
          </div>

          <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} className="flex items-center gap-1.5 rounded-full border border-white/25 px-3 py-1.5 text-xs font-bold text-white transition-colors duration-150 ease-ville hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-1134" data-mp="1vfc13v5xlnds">
            Toutes les étapes
            <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform duration-150 ease-ville ${open ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-1135" data-mp="1gntmo1sq00wj" />
          </button>
        </div>
      </div>

      <AnimatePresence initial={false} data-id="element-1136" data-mp="2s1xh1937zr24">
        {open && <motion.div key="demo-steps" initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} className="absolute left-0 right-0 top-full max-h-[70vh] overflow-y-auto border-t border-white/10 bg-ville-deep shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)]" data-id="element-1137" data-mp="gfn8n88xe3x7">
            <div className="mx-auto max-w-[1500px] px-5 py-5 lg:px-10" data-id="element-1138" data-mp="3tv1qg45wfzu5">
              <div className="flex flex-wrap items-baseline justify-between gap-3" data-id="element-1139" data-mp="3ovoturp87eed">
                <p className="max-w-2xl text-sm text-white/70" data-id="element-1140" data-mp="3oy1j7pxbcr1g">{demoIntro}</p>
                <button type="button" onClick={restart} className="flex items-center gap-1.5 text-xs font-bold text-ville-lime transition-colors duration-150 ease-ville hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-1141" data-mp="3kudb964rrtfo">
                  <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1142" data-mp="1e1t22rzvb4hp" />
                  Recommencer la visite
                </button>
              </div>

              {CHAPTER_ORDER.map(chapterId => {
            const chapter = demoChapters[chapterId];
            const isFocus = chapterId === 'loisirs';
            return <section key={chapterId} className="mt-6 first:mt-5" data-id="element-1143" data-mp="1t7gmlbk714ny">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1" data-id="element-1144" data-mp="2gvq4w3i77aac">
                      <h3 className={`text-xs font-extrabold uppercase tracking-[0.14em] ${isFocus ? 'text-ville-lime' : 'text-white/45'}`} data-id="element-1145" data-mp="2kxl32z4u17jw">
                        {chapter.label}
                      </h3>
                      <p className="text-xs text-white/50" data-id="element-1146" data-mp="vj6lqzw58myf">{chapter.hint}</p>
                    </div>

                    <ol className="mt-2 divide-y divide-white/10" data-id="element-1147" data-mp="10ij53ant3bw9">
                      {demoSteps.map((item, itemIndex) => {
                  if (item.chapter !== chapterId) return null;
                  const isCurrent = itemIndex === index;
                  const isVisited = visited.includes(item.id);
                  return <li key={item.id} data-id="element-1148" data-mp="11x2l5eeqk18b" data-mpi={itemIndex}>
                            <button type="button" onClick={() => {
                      goTo(itemIndex);
                      setOpen(false);
                    }} aria-current={isCurrent ? 'step' : undefined} className={`flex w-full items-start gap-4 px-2 py-3 text-left transition-colors duration-150 ease-ville hover:bg-white/[0.06] focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${isCurrent ? 'bg-white/[0.08]' : ''}`} data-id="element-1149" data-mp="wzu27blrjt60">
                              <span className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-extrabold ${isCurrent ? 'bg-ville-lime text-ville-deep' : isVisited ? 'bg-white/15 text-white' : 'border border-white/25 text-white/60'}`} data-id="element-1150" data-mp="2x4zjpv3s0v39">
                                {isVisited && !isCurrent ? <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1151" data-mp="3ovdqz8hab95y" /> : itemIndex + 1}
                              </span>
                              <span className="min-w-0" data-id="element-1152" data-mp="2qa3xt50g3tq">
                                <span className="block text-sm font-bold text-white" data-id="element-1153" data-mp="7mqusv3lgwai">{item.module}</span>
                                <span className="mt-0.5 block text-sm text-white/75" data-id="element-1154" data-mp="1s6m503c73n5b">{item.action}</span>
                                <span className="mt-0.5 block text-xs text-white/50" data-id="element-1155" data-mp="1n5gemc60cbyy">{item.result}</span>
                              </span>
                            </button>
                          </li>;
                })}
                    </ol>
                  </section>;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}